import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '@/components/ui/dialog';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Copy, 
  Eye, 
  Edit, 
  Trash2, 
  Building2, 
  User, 
  Mail, 
  Phone,
  Calendar,
  MousePointer,
  CheckCircle,
  XCircle,
  RefreshCw
} from 'lucide-react';

interface Prospect {
  id: string;
  first_name: string;
  last_name?: string;
  company_name: string;
  email?: string;
  phone?: string;
  job_title?: string;
  industry?: string;
  short_id: string;
  original_url: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  click_count: number;
  last_clicked_at?: string;
  notes?: string;
  is_customer?: boolean;
}

interface ProspectFormData {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phone: string;
  jobTitle: string;
  industry: string;
  originalUrl: string;
  notes: string;
}

const ProspectManagementTab = () => {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProspect, setEditingProspect] = useState<Prospect | null>(null);
  const [formData, setFormData] = useState<ProspectFormData>({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    phone: '',
    jobTitle: '',
    industry: '',
    originalUrl: '/request-demo',
    notes: ''
  });

  useEffect(() => {
    fetchProspects();
  }, []);

  const fetchProspects = async () => {
    try {
      const { data, error } = await supabase
        .from('prospects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProspects(data || []);
    } catch (error) {
      toast.error('Failed to fetch prospects');
      console.error('Error fetching prospects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.firstName || !formData.companyName) {
      toast.error('First name and company name are required');
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('You must be logged in to manage prospects');
        return;
      }

      if (editingProspect) {
        // Update existing prospect
        const { error: updateError } = await supabase
          .from('prospects')
          .update({
            first_name: formData.firstName,
            last_name: formData.lastName || null,
            company_name: formData.companyName,
            email: formData.email || null,
            phone: formData.phone || null,
            job_title: formData.jobTitle || null,
            industry: formData.industry || null,
            original_url: formData.originalUrl,
            notes: formData.notes || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingProspect.id);

        if (updateError) {
          throw new Error(updateError.message || 'Failed to update prospect');
        }

        toast.success(`Prospect ${formData.firstName} updated successfully!`);
      } else {
        // Create new prospect
        const { data: result, error: functionError } = await supabase.functions.invoke('create-prospect-url', {
          body: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            companyName: formData.companyName,
            email: formData.email,
            phone: formData.phone,
            jobTitle: formData.jobTitle,
            industry: formData.industry,
            originalUrl: formData.originalUrl,
            notes: formData.notes
          }
        });

        if (functionError) {
          throw new Error(functionError.message || 'Failed to create prospect');
        }
        
        toast.success(
          `Personalized URL created for ${formData.firstName}!`,
          {
            action: {
              label: 'Copy URL',
              onClick: () => copyToClipboard(result.personalized_url)
            }
          }
        );
      }

      // Reset form and close dialog
      setFormData({
        firstName: '',
        lastName: '',
        companyName: '',
        email: '',
        phone: '',
        jobTitle: '',
        industry: '',
        originalUrl: '/request-demo',
        notes: ''
      });
      setIsDialogOpen(false);
      setEditingProspect(null);
      
      // Refresh prospects list
      fetchProspects();
    } catch (error) {
      const action = editingProspect ? 'update' : 'create';
      toast.error(error instanceof Error ? error.message : `Failed to ${action} prospect`);
      console.error(`Error ${action}ing prospect:`, error);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('URL copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  const openPersonalizedPage = (shortId: string) => {
    const url = `${window.location.origin}/p/${shortId}`;
    window.open(url, '_blank');
  };

  const handleEdit = (prospect: Prospect) => {
    setEditingProspect(prospect);
    setFormData({
      firstName: prospect.first_name,
      lastName: prospect.last_name || '',
      companyName: prospect.company_name,
      email: prospect.email || '',
      phone: prospect.phone || '',
      jobTitle: prospect.job_title || '',
      industry: prospect.industry || '',
      originalUrl: prospect.original_url || '/request-demo',
      notes: prospect.notes || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (prospect: Prospect) => {
    if (!confirm(`Are you sure you want to delete the prospect for ${prospect.first_name} at ${prospect.company_name}?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('prospects')
        .delete()
        .eq('id', prospect.id);

      if (error) throw error;

      toast.success('Prospect deleted successfully');
      fetchProspects();
    } catch (error) {
      toast.error('Failed to delete prospect');
      console.error('Error deleting prospect:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      companyName: '',
      email: '',
      phone: '',
      jobTitle: '',
      industry: '',
      originalUrl: '/request-demo',
      notes: ''
    });
    setEditingProspect(null);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center">Loading prospects...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Prospect Management</h2>
          <p className="text-gray-600 mt-2">Create and manage personalized landing pages for prospects</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              New Prospect
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingProspect ? 'Edit Prospect' : 'Create New Prospect'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  placeholder="John"
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  placeholder="Doe"
                />
              </div>
              <div>
                <Label htmlFor="companyName">Company Name *</Label>
                <Input
                  id="companyName"
                  value={formData.companyName}
                  onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                  placeholder="TechCorp"
                />
              </div>
              <div>
                <Label htmlFor="jobTitle">Job Title</Label>
                <Input
                  id="jobTitle"
                  value={formData.jobTitle}
                  onChange={(e) => setFormData({...formData, jobTitle: e.target.value})}
                  placeholder="Hiring Manager"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="john@techcorp.com"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <div>
                <Label htmlFor="industry">Industry</Label>
                <Input
                  id="industry"
                  value={formData.industry}
                  onChange={(e) => setFormData({...formData, industry: e.target.value})}
                  placeholder="Technology"
                />
              </div>
              <div>
                <Label htmlFor="originalUrl">Redirect URL</Label>
                <Input
                  id="originalUrl"
                  value={formData.originalUrl}
                  onChange={(e) => setFormData({...formData, originalUrl: e.target.value})}
                  placeholder="/request-demo"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes about this prospect..."
                  rows={3}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit}>
                {editingProspect ? 'Update Prospect' : 'Create Prospect'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Prospects ({prospects.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Short ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Clicks</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {prospects.map((prospect) => (
                <TableRow key={prospect.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-500" />
                      <div>
                        <div className="font-medium">
                          {prospect.first_name} {prospect.last_name}
                        </div>
                        {prospect.job_title && (
                          <div className="text-sm text-gray-500">{prospect.job_title}</div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-500" />
                      <div>
                        <div className="font-medium">{prospect.company_name}</div>
                        {prospect.industry && (
                          <div className="text-sm text-gray-500">{prospect.industry}</div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {prospect.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-3 h-3" />
                          <span>{prospect.email}</span>
                        </div>
                      )}
                      {prospect.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-3 h-3" />
                          <span>{prospect.phone}</span>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                        {prospect.short_id}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(`${window.location.origin}/p/${prospect.short_id}`)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {prospect.is_customer ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <Badge variant="default" className="bg-green-100 text-green-800">
                            Customer
                          </Badge>
                        </>
                      ) : (
                        <>
                          <User className="w-4 h-4 text-blue-500" />
                          <Badge variant="outline" className="text-blue-600">
                            Prospect
                          </Badge>
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <MousePointer className="w-4 h-4 text-gray-500" />
                      <span className="font-medium">{prospect.click_count}</span>
                      {prospect.last_clicked_at && (
                        <div className="text-xs text-gray-500">
                          Last: {new Date(prospect.last_clicked_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <div className="text-sm">
                        {new Date(prospect.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPersonalizedPage(prospect.short_id)}
                        title="View personalized page"
                      >
                        <Eye className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(prospect)}
                        title="Edit prospect"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(prospect)}
                        title="Delete prospect"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {prospects.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No prospects found. Create your first personalized landing page!
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProspectManagementTab; 